/* Teevero occasion templates: ?template=thanksgiving | christmas on /pages/design (tv-designer.js) and /pages/order
   (tv-order.js), the same way on both. A template is the ?text= opening (the shopper's to edit, in the page's own
   lettering looks and inks) plus built-in art and a year line, on a preselected shirt color:
   - Art: a FabricImage with tvKind 'template' and tvArt = an id on the API's allow-list (apps/designer-api
     src/designs/spec.ts TEMPLATE_ART). The page sends it as an art entry { layer, _design_id: id }; the API prints it from
     its own 2400 px file at 300 DPI, so it needs no upload, account or art review. Same PNG here (theme asset).
   - Layout = the ads' print (render/holiday-print.mjs T): art top-center 4 % from the top at artW of the print area's
     width, the text block 90 % of the width (last line in the accent ink, the classic look), the year 34 % (Wide).
     The whole stack shrinks together when an area is too short for it (hoodies), so it always sits inside with a margin.
   - refit(): the designer keeps print positions relative to the area's width when the shirt style changes; a design
     with template art is then shrunk as a whole if it would leave a shorter area.
   Without ?template= (or with an unknown one) window.TeeveroTemplates.active is null and the pages behave as before. */
(() => {
  const T = {
    thanksgiving: { art: 'tpl-pumpkin-2026', file: 'tv-tpl-pumpkin-2026.png', name: 'Pumpkin art', artW: 0.56, text: 'Family Thanksgiving', year: '2026', color: 'Sand' },
    christmas: { art: 'tpl-tree-2026', file: 'tv-tpl-tree-2026.png', name: 'Christmas tree art', artW: 0.46, text: 'Family Christmas', year: '2026', color: 'Red' },
  };
  const id = (new URLSearchParams(location.search).get('template') || '').toLowerCase().replace(/[^a-z]/g, '');
  const t = Object.hasOwn(T, id) ? { id, ...T[id] } : null;
  const YEAR = /^(19|20)\d\d$/;
  const TOP = 0.04, TEXT_W = 0.9, YEAR_W = 0.34, GAP_T = 0.0167, GAP_Y = 0.0194, BOTTOM = 0.03; // shares of the area (gaps: of its width)

  // Rough printed box of a center-origin layer from its own numbers (works on Fabric objects and their JSON alike).
  const roughRect = (o) => {
    const w = (o.width || 0) * Math.abs(o.scaleX || 1), h = (o.height || 0) * Math.abs(o.scaleY || 1);
    const r = ((o.angle || 0) * Math.PI) / 180, c = Math.abs(Math.cos(r)), s = Math.abs(Math.sin(r));
    const bw = w * c + h * s, bh = w * s + h * c;
    return { left: o.left - bw / 2, top: o.top - bh / 2, width: bw, height: bh };
  };
  const union = (rs) => {
    const x0 = Math.min(...rs.map((r) => r.left)), y0 = Math.min(...rs.map((r) => r.top));
    return { left: x0, top: y0, width: Math.max(...rs.map((r) => r.left + r.width)) - x0, height: Math.max(...rs.map((r) => r.top + r.height)) - y0 };
  };
  // Scales a set of layers by k about (cx, cy), then moves them by (dx, dy).
  const move = (objs, k, cx, cy, dx = 0, dy = 0) => {
    for (const o of objs) {
      o.left = cx + (o.left - cx) * k + dx; o.top = cy + (o.top - cy) * k + dy;
      o.scaleX *= k; o.scaleY *= k;
      if (o.tvWant) o.tvWant *= k;
      o.setCoords?.();
    }
  };

  /** The template's layers in order (art, the page's text layers, year), laid out in print area a.
   *  ctx: { F, a, makeText, loadFont, decorate, uid, asset, box } from the page; texts: the page's opening text layers
   *  (its starter, already fitted somewhere in the area); year: the year line's text ('' = none). */
  const dress = async (ctx, texts, year = t.year) => {
    const { F, a, box } = ctx;
    const img = await F.FabricImage.fromURL(ctx.asset(t.file), { crossOrigin: 'anonymous' });
    Object.assign(img, { tvId: ctx.uid(), tvKind: 'template', tvArt: t.art, tvName: t.name });
    ctx.decorate(img);
    let yo = null;
    if (year) { await ctx.loadFont('wide'); yo = ctx.makeText(year, { font: 'wide' }); }
    // Sizes at full width, then one factor K for the whole stack so it fits the area's height.
    const artK = (a.w * t.artW) / img.width, artH = img.height * artK;
    const tr = texts.length ? union(texts.map(box)) : null;
    const textK = tr ? (a.w * TEXT_W) / tr.width : 0, textH = tr ? tr.height * textK : 0;
    const yr = yo && box(yo), yearK = yr ? (a.w * YEAR_W) / yr.width : 0, yearH = yr ? yr.height * yearK : 0;
    const gT = tr ? a.w * GAP_T : 0, gY = yr ? a.w * GAP_Y : 0;
    const top = a.y + a.h * TOP, room = a.y + a.h * (1 - BOTTOM) - top;
    const K = Math.min(1, room / (artH + gT + textH + gY + yearH));
    const cx = a.x + a.w / 2;
    let y = top;
    img.set({ scaleX: artK * K, scaleY: artK * K, left: cx, top: y + (artH * K) / 2 });
    img.setCoords();
    y += artH * K + gT * K;
    if (tr) { move(texts, textK * K, tr.left + tr.width / 2, tr.top + tr.height / 2, cx - (tr.left + tr.width / 2), y + (textH * K) / 2 - (tr.top + tr.height / 2)); y += textH * K + gY * K; }
    if (yr) move([yo], yearK * K, yr.left + yr.width / 2, yr.top + yr.height / 2, cx - (yr.left + yr.width / 2), y + (yearH * K) / 2 - (yr.top + yr.height / 2));
    const all = [img, ...texts, ...(yo ? [yo] : [])];
    for (const o of all) o.tvWant = o.scaleX; // the designer's auto-fit grows text back up to this size at most
    return all;
  };

  /** Designer, style change: layers with template art that would leave the new (shorter) area shrink together, centered. */
  const refit = (objs, a) => {
    if (!objs.some((o) => o.tvKind === 'template')) return;
    const m = Math.min(a.w, a.h) * 0.02, u = union(objs.map(roughRect));
    const k = Math.min(1, (a.w - 2 * m) / u.width, (a.h - 2 * m) / u.height);
    const ux = u.left + u.width / 2, uy = u.top + u.height / 2;
    const nw = u.width * k, nh = u.height * k;
    const nx = Math.min(Math.max(ux, a.x + m + nw / 2), a.x + a.w - m - nw / 2), ny = Math.min(Math.max(uy, a.y + m + nh / 2), a.y + a.h - m - nh / 2);
    if (k < 1 || nx !== ux || ny !== uy) move(objs, k, ux, uy, nx - ux, ny - uy);
  };

  // Order page: its one text field holds the whole wording; a last word that is a year ("… 2026") prints as the year line.
  const split = (text) => {
    const w = String(text || '').trim().split(/\s+/).filter(Boolean);
    return w.length && YEAR.test(w[w.length - 1]) ? { words: w.slice(0, -1).join(' '), year: w[w.length - 1] } : { words: w.join(' '), year: '' };
  };
  // Order page, classic look: the template's own text block, as the designer opens it and the ads show it: everything but
  // the last word on the first line, the last word ("Thanksgiving") alone in the accent ink. Other looks use the page's starter.
  const lines = async (ctx, words) => {
    const w = words.split(/\s+/).filter(Boolean);
    if (w.length < 2) return null;
    const head = w.slice(0, -1).join(' '), last = w[w.length - 1];
    await ctx.loadFont('display');
    const o = ctx.makeText(`${head}\n${last}`, { font: 'display' });
    o.set({ styles: { 1: Object.fromEntries([...last].map((_, j) => [j, { fill: ctx.inks().accent }])) } });
    return [o];
  };

  // Order page: a line under the text field says the art is on the shirt, with Remove / Put back (the designer deletes it like any layer).
  const note = (anchor, onToggle) => {
    if (!anchor) return;
    const box = document.createElement('p');
    box.className = 'tv-tpl-note';
    box.setAttribute('role', 'status');
    box.style.cssText = 'display:flex;flex-wrap:wrap;align-items:center;gap:.5em;margin:.6em 0 0;font-size:.92em';
    const img = Object.assign(document.createElement('img'), { src: anchor.closest('[data-assets]')?.dataset.assets + t.file, alt: '', width: 28, height: 28 });
    img.style.cssText = 'width:28px;height:28px;object-fit:contain';
    const msg = document.createElement('span');
    const btn = Object.assign(document.createElement('button'), { type: 'button' });
    btn.style.cssText = 'font:inherit;text-decoration:underline;background:none;border:0;padding:.4em .2em;color:inherit;cursor:pointer;min-height:44px';
    let on = true;
    const show = () => { msg.textContent = on ? `${t.name} is on your shirt.` : `${t.name} removed.`; btn.textContent = on ? 'Remove art' : 'Put it back'; };
    btn.addEventListener('click', () => { on = !on; show(); onToggle(on); });
    show();
    box.append(img, msg, btn);
    anchor.after(box);
  };

  window.TeeveroTemplates = { active: t, dress, refit, split, lines, note };
})();
